<template>
    <div>
        <b-card v-if="isLogMessage" bg-variant="light" body-class="p-2" class="mb-2 object-activity-log">
            <div class="d-flex justify-content-between align-items-start">
                <div>
                    <i class="fas fa-info-circle text-info mr-2"></i>
                    <strong>
                        <EmployeeLink
                            v-if="item.User || item.user_id"
                            :user="item.User"
                            :user-id="item.user_id"
                            fallback="Автор не зафиксирован"
                            name-order="last-first"
                        />
                        <span v-else>{{ actorFallback }}</span>
                    </strong>
                    <span class="ml-2">{{ actionText(item.action) }}</span>
                </div>
                <small class="text-muted object-activity-date">{{ formatDate(item.created_at) }}</small>
            </div>
            <div v-if="item.description" class="mt-1 ml-4">
                <DiscussionHistory :value="item.description" source-type="object_history" :source-id="item.id" />
            </div>
            <div v-if="formattedChanges.length" class="object-change-list ml-4 mt-2">
                <div
                    v-for="change in formattedChanges"
                    :key="`${item.id}_${change.field}`"
                    class="object-change-line"
                >
                    <span class="object-change-label">{{ change.label || change.field }}</span>
                    <span class="object-change-value">{{ change.old_value }}</span>
                    <i class="fas fa-arrow-right text-muted"></i>
                    <span class="object-change-value object-change-value--new">{{ change.new_value }}</span>
                </div>
            </div>
        </b-card>

        <div v-else class="object-comment border-bottom pb-3 mb-3">
            <div class="d-flex justify-content-between align-items-start">
                <div>
                    <strong>
                        <EmployeeLink
                            :user="item.Author"
                            :user-id="item.user_id"
                            fallback="Автор не зафиксирован"
                            name-order="last-first"
                        />
                    </strong>
                    <small class="text-muted ml-2 object-activity-date">{{ formatDate(item.created_at) }}</small>
                    <b-badge v-if="item.is_system_comment" variant="info" class="ml-2">Системный</b-badge>
                </div>
                <div v-if="canEdit && !item.is_system_comment">
                    <i
                        v-if="!isEdit"
                        class="fas fa-pen text-success small mr-2 object-clickable"
                        title="Редактировать"
                        @click="startEdit"
                    ></i>
                    <i
                        v-if="!isEdit"
                        class="fas fa-trash-alt text-danger small object-clickable"
                        title="Удалить"
                        @click="removeComment"
                    ></i>
                </div>
            </div>

            <DiscussionText v-if="!isEdit" :value="item.comment_text || ''" source-type="object_comment" :source-id="item.id" />

            <div v-else class="mt-2">
                <DiscussionEditor source-type="object_comment" :source-id="item.id" @saved="cancelEdit"
                    v-model="editedText"
                    placeholder="Редактировать комментарий"
                ></DiscussionEditor>
                <div class="mt-2">
                    <b-button size="sm" variant="success" @click="saveEdit">
                        <i class="fas fa-check"></i>
                        Сохранить
                    </b-button>
                    <b-button size="sm" variant="outline-secondary" class="ml-2" @click="cancelEdit">
                        <i class="fas fa-times"></i>
                        Отмена
                    </b-button>
                </div>
            </div>

            <div v-if="item.Files && item.Files.length" class="mt-2">
                <small class="text-muted">
                    <i class="fas fa-paperclip"></i>
                    Прикрепленные файлы:
                </small>
                <ObjectFileList
                    :list="item.Files"
                    :can-edit="canEdit && !item.is_system_comment"
                    :object-id="objectId"
                    @remove="removeFile"
                />
            </div>
        </div>
    </div>
</template>

<script>
import EmployeeLink from '@/components/EmployeeLink.vue';
import linkifyText from '@/utils/Urlify';
import ObjectFileList from './ObjectFileList.vue';

const ACTION_TEXT = {
    created: `создал объект`,
    legacy_history_object_created: `добавил объект из архива табелей`,
    legacy_history_objects_confirmed: `сопоставил объект с архивом табелей`,
    updated: `изменил объект`,
    process_created: `перевел объект в работу`,
    process_updated: `изменил рабочий процесс`,
    archived: `переместил объект в архив`,
    reactivated: `вернул объект в работу`,
    comment_edited: `изменил комментарий`,
    comment_deleted: `удалил комментарий`,
    file_deleted: `удалил файл`,
    file_added: `добавил файл`,
    object_access_updated: `изменил доступ к объекту`,
    target_created: `добавил договорную работу`,
    target_updated: `изменил договорную работу`,
    target_deleted: `удалил договорную работу`,
    material_target_created: `добавил материал к работе`,
    material_target_updated: `изменил материал по договору`,
    material_target_deleted: `удалил материал из работы`,
    process_deleted: `удалил рабочий процесс`
};

export default {
    name: `ObjectActivityItem`,
    components: {
        EmployeeLink,
        ObjectFileList
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        canEdit: {
            type: Boolean,
            default: false
        },
        objectId: {
            type: [Number, String],
            required: true
        },
        users: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            isEdit: false,
            editedText: ``
        };
    },
    computed: {
        isLogMessage() {
            return Boolean(this.item.action || this.item.type === `log`);
        },
        actorFallback() {
            return this.item.actor_type === `system` ? `Система` : `Автор не зафиксирован`;
        },
        changes() {
            return Array.isArray(this.item.changes) ? this.item.changes : [];
        },
        formattedChanges() {
            return this.changes.filter(change => change.field !== 'history_import').map(change => Object.assign({}, change, {
                old_value: this.formatChangeValue(change, change.old_value),
                new_value: this.formatChangeValue(change, change.new_value)
            }));
        }
    },
    methods: {
        formatLinkedText(text) {
            return linkifyText(text || ``);
        },
        formatDate(value) {
            if (!value) {
                return ``;
            }
            const date = new Date(value);
            if (!Number.isFinite(date.getTime())) {
                return value;
            }
            const datePart = date.toLocaleDateString(`ru-RU`, {
                day: `2-digit`,
                month: `2-digit`,
                year: `numeric`
            });
            const weekday = date.toLocaleDateString(`ru-RU`, {weekday: `short`}).replace(`.`, ``);
            const timePart = date.toLocaleTimeString(`ru-RU`, {
                hour: `2-digit`,
                minute: `2-digit`
            });
            return `${datePart} ${weekday}, ${timePart}`;
        },
        actionText(action) {
            return ACTION_TEXT[action] || action || `добавил событие`;
        },
        userById(userId) {
            const id = Number(userId);
            if (!Number.isInteger(id) || id <= 0) {
                return null;
            }
            return (this.users || []).find(user => Number(user.user_id) === id) || null;
        },
        userNameById(userId) {
            const user = this.userById(userId);
            if (!user) {
                return userId ? `Пользователь #${userId}` : `Не указан`;
            }
            return user.full_name || `${user.last_name || ``} ${user.first_name || ``}`.trim() || `Пользователь #${user.user_id}`;
        },
        formatUserListValue(value) {
            const text = String(value || ``).trim();
            if (!text || text === `Нет` || text === `Не указано`) {
                return text || `Нет`;
            }
            const ids = text
                .split(/[,;]/)
                .map(part => Number(String(part).replace(`#`, ``).trim()))
                .filter(id => Number.isInteger(id) && id > 0);
            if (!ids.length) {
                return text;
            }
            return ids.map(id => this.userNameById(id)).join(`, `);
        },
        formatChangeValue(change, value) {
            const field = change && change.field;
            if (field === `curator_id` || field === `main_staff_id`) {
                const text = String(value || ``).trim();
                if (!text || text === `Не указано`) {
                    return `Не указан`;
                }
                if (!Number.isInteger(Number(text))) {
                    return text;
                }
                return this.userNameById(text);
            }
            if (field === `has_access`) {
                return this.formatUserListValue(value);
            }
            if (field === `gallery_access_config` || field === `object_access_config`) {
                return String(value || ``).replace(/#(\d+)/g, (match, userId) => this.userNameById(userId));
            }
            return value;
        },
        startEdit() {
            this.editedText = this.item.comment_text || ``;
            this.isEdit = true;
        },
        cancelEdit() {
            this.isEdit = false;
            this.editedText = ``;
        },
        saveEdit() {
            if (!this.editedText.trim()) {
                this.$bvToast.toast(`Комментарий не может быть пустым`, {
                    title: `Ошибка`,
                    variant: `danger`,
                    solid: true
                });
                return;
            }
            this.$emit(`editComment`, {
                id: this.item.id,
                text: this.editedText
            });
        },
        removeComment() {
            this.$bvModal.msgBoxConfirm(`Вы уверены, что хотите удалить комментарий?`, {
                title: `Подтверждение удаления`,
                okVariant: `danger`,
                okTitle: `Удалить`,
                cancelTitle: `Отмена`
            }).then(confirmed => {
                if (confirmed) {
                    this.$emit(`removeComment`, this.item.id);
                }
            });
        },
        removeFile(fileId) {
            this.$emit(`removeFile`, {
                commentId: this.item.id,
                fileId
            });
        },
        hasDirtyState() {
            return this.isEdit && this.editedText !== (this.item.comment_text || ``);
        }
    }
};
</script>

<style scoped>
.object-clickable {
    cursor: pointer;
}

.object-clickable:hover {
    opacity: 0.75;
}

.object-comment-text {
    white-space: pre-wrap;
}

.object-linked-text {
    overflow-wrap: anywhere;
}

.object-change-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.object-change-line {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 0.82rem;
}

.object-change-label {
    font-weight: 600;
    color: #495057;
}

.object-change-value {
    padding: 1px 5px;
    border-radius: 4px;
    background: #f1f3f5;
    color: #495057;
}

.object-change-value--new {
    background: #e7f5ff;
    color: #0b7285;
}

.object-activity-date {
    white-space: nowrap;
}

@media (max-width: 576px) {
    .object-activity-log .d-flex,
    .object-comment > .d-flex {
        gap: 4px;
        flex-direction: column;
    }

    .object-activity-date {
        white-space: normal;
    }
}
</style>
